/* eslint-disable no-restricted-imports -- firebase compat: 新たにコードを追加する時は modular API を使う。modular API へ移行したらこのコメントごと削除する */
import firebase from "firebase/compat/app";
import "firebase/compat/firestore";
/* eslint-enable no-restricted-imports */
import { EventVideoSchema } from "firestore/types";
import useSWR from "swr";

import type { EventVideo } from "@spwn/types/firebase/firestore";
import type { SWRResponse } from "swr";

const generateKey = ({
  eventId,
  videoIds,
}: {
  eventId: string;
  videoIds: string[];
}) => ({
  path: "getStreamingVideos",
  eventId,
  // SWR のキーを安定させるためソートして結合する
  videoIds: [...videoIds].sort().join(","),
});

type UseGetStreamingVideos = (args: {
  eventId?: string;
  videoIds: string[];
}) => SWRResponse<{ [videoId: string]: EventVideo }>;

/**
 * 複数の videoId に対応する EventVideo を取得して videoId -> EventVideo のマップで返す。
 * マルチアングルの eventVideoMap を構築するために利用する。
 *
 * 取得したドキュメントは EventVideoSchema で検証し、不正なものは除外する。
 */
export const useGetStreamingVideos: UseGetStreamingVideos = ({
  eventId,
  videoIds,
}) => {
  return useSWR(
    eventId && videoIds.length > 0 ? generateKey({ eventId, videoIds }) : null,
    async () => {
      const entries = await Promise.all(
        videoIds.map(async (videoId) => {
          const snapshot = await firebase
            .firestore()
            .collection("streaming")
            .doc(eventId)
            .collection("videos")
            .doc(videoId)
            .get();
          // ドキュメントが存在しない場合は _id だけの不完全なオブジェクトが
          // 検証を通り得るため、先に存在チェックして除外する。
          const data = snapshot.data();
          if (!snapshot.exists || !data) {
            console.error(`Streaming video not found: ${videoId}`);
            return null;
          }
          const result = EventVideoSchema.safeParse({
            ...data,
            _id: snapshot.id,
          });
          if (!result.success) {
            console.error(
              `Failed to parse streaming video ${videoId}:`,
              result.error
            );
            return null;
          }
          return [videoId, result.data] as const;
        })
      );
      return Object.fromEntries(entries.filter((entry) => entry !== null));
    }
  );
};
